import { useCallback, useDeferredValue, useEffect, useMemo, useRef } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';
const { useAppContext } = MojitoCore.Presentation.Hooks;
const { PAGE_READY, PAGE_INTERACTED } = MojitoCore.Services.Performance.types.TIMELINE_RECORD_TYPES;
const { useComponentVisibility } = MojitoPresentation.Hooks;

const awaitingDataLoadClients = new Map();
const awaitingToMountClients = new Map();
const AWAITING_DELAY = 100;
// This delay can be set to AWAITING_DELAY if needed to slightly postpone PAGE_READY reporting
// (see useLoadingStatusMetric noContent argument doc for explanation).
let situationalDelay = 0;

/**
 * Contains custom hooks for performance monitoring and reporting.
 *
 * @class Hooks
 * @memberof Mojito.Modules.Performance
 */

/**
 * Hook keeps track on clients (components) that are still in pending state and are waiting for a data to be rendered.
 * It will return function that should be called in order to sync (add) clients in awaiting registry once data has been arrived.
 *
 * @function useAwaitingClientsRegistry
 *
 * @param {string} name - Name of the component.
 * @param {boolean} loadDone - Indicates when loading is done with true, otherwise false.
 * @param {string} [instanceId] - The id of component instance.
 *
 * @returns {Function} Function should be called to sync awaiting clients in registry. It accepts load done flag as an argument.
 * @memberof Mojito.Modules.Performance.Hooks
 */
export const useAwaitingClientsRegistry = (name, loadDone, instanceId) => {
    useMemo(() => {
        instanceId && awaitingToMountClients.set(instanceId, name);
    }, [instanceId, name]);

    useEffect(() => {
        awaitingToMountClients.delete(instanceId);
        return () => {
            awaitingToMountClients.delete(instanceId);
            awaitingDataLoadClients.delete(instanceId);
        };
    }, [instanceId]);

    return useCallback(() => {
        if (instanceId && !loadDone) {
            awaitingDataLoadClients.set(instanceId, name);
        }
    }, [instanceId, loadDone, name]);
};

/**
 * A custom React hook that logs global user interactions with the application. Quasi singleton.
 *
 * This hook sets up event listeners for common user interactions (touch, scroll)
 * and logs the first interaction that occurs. After the first interaction is logged, all event listeners
 * are removed to prevent further logging.
 *
 * @function useGlobalInteractionMetric
 *
 * @param {boolean} eventsToRegister - Indicates when loading is done with true, otherwise false.
 *
 * @memberof Mojito.Modules.Performance.Hooks
 */
export const useGlobalInteractionMetric = eventsToRegister => {
    const abortControllerRef = useRef(new AbortController());
    const { performanceEmitter } = useAppContext();

    const onInteraction = useCallback(
        event => {
            abortControllerRef.current.abort();
            performanceEmitter.emitPerformanceMetric(PAGE_INTERACTED, { eventType: event.type });
        },
        [performanceEmitter]
    );

    useMemo(() => {
        eventsToRegister.forEach(eventType => {
            const abortSignal = abortControllerRef.current.signal;
            window.addEventListener(eventType, onInteraction, {
                passive: true,
                signal: abortSignal,
            });
        });
    }, [eventsToRegister, onInteraction]);

    useEffect(() => () => abortControllerRef.current.abort(), []);
};

/**
 * Deletes an entry of the list of loading components if available and does reporting.
 *
 * @function useAwaitingClientsLoadDone
 *
 * @param {boolean} loadDone - Indicates when loading is done with true, otherwise false.
 * @param {string} [instanceId] - Component instance id.
 * @param {boolean} [noContent = false] - Boolean indicating if there is no content available. If set to true then hook will apply 100ms delay
 * for reporting PAGE_READY metric. Not having content, typically, forces component to unmount. In that case the underneath components might come into view port and request content.
 * This delay is needed to make sure that these components have chance to register themselves once appear in view port.
 *
 * @memberof Mojito.Modules.Performance.Hooks
 */
export const useAwaitingClientsLoadDone = (loadDone, instanceId, noContent) => {
    const loadDoneDeferred = useDeferredValue(loadDone);
    const { performanceEmitter, pathname } = useAppContext();
    const emitterTimeout = useRef();
    useEffect(() => {
        if (loadDoneDeferred) {
            awaitingDataLoadClients.delete(instanceId);
            const isReady = () =>
                awaitingDataLoadClients.size === 0 && awaitingToMountClients.size === 0;
            if (isReady() && instanceId) {
                situationalDelay = noContent ? AWAITING_DELAY : situationalDelay;
                clearTimeout(emitterTimeout.current);
                emitterTimeout.current = setTimeout(() => {
                    if (isReady()) {
                        emitPageReady(performanceEmitter, pathname);
                        situationalDelay = 0;
                    }
                }, situationalDelay);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadDoneDeferred, instanceId, awaitingToMountClients.size, noContent]);
};

/**
 * The hook will report {@link Mojito.Core.Services.Performance.types.TIMELINE_RECORD_TYPES.PAGE_READY|PAGE_READY} metric
 * once all registered clients are rendered and completed content loading.
 *
 * @function useLoadingStatusMetric
 *
 * @param {boolean} loadDone - Indicates when loading is done with true, otherwise false.
 * @param {string} instanceId - Component instance id.
 * @param {string} name - Name of the component.
 * @param {boolean} [noContent = false] - Boolean indicating if there is no content available. If set to true then hook will apply 100ms delay
 * for reporting PAGE_READY metric. Not having content, typically, forces component to unmount. In that case the underneath components might come into view port and request content.
 * This delay is needed to make sure that these components have chance to register themselves once appear in view port.
 *
 * @returns {React.RefObject|Function} Reference to the client element.
 * @memberof Mojito.Modules.Performance.Hooks
 */
export const useLoadingStatusMetric = (loadDone, instanceId, name, noContent) => {
    const syncAwaitingClients = useAwaitingClientsRegistry(name, loadDone, instanceId);
    const elementRef = useComponentVisibility(syncAwaitingClients);
    useAwaitingClientsLoadDone(loadDone, instanceId, noContent);
    return elementRef;
};

const emitPageReady = (performanceEmitter, pathname) => {
    const timestamp = performance.now();
    performanceEmitter.emitPerformanceMetric(PAGE_READY, { pathname, timestamp });
};
